export const blogs = [
  {
    id: 1,
    slug: "which-wire-for-air-conditioner-india",
    title: "Which Wire is Best for Air Conditioner? Complete Guide for Indian Homes",
    excerpt:
      "Using undersized or uncertified wire for your AC can trip MCBs, overheat walls, and cause fires. Here's exactly which wire you need for 1 ton, 1.5 ton and 2 ton ACs — and why it matters.",
    category: "Wiring Guides",
    date: "May 12, 2025",
    readTime: "7 min read",
    image: null,
    content: [
      {
        type: "p",
        text: "Every summer, thousands of Indian homes report electrical trips, burning smells from walls, and in the worst cases — fires. A significant portion of these incidents trace back to one overlooked decision: the wire used for the air conditioner circuit.",
      },
      {
        type: "p",
        text: "Unlike a ceiling fan or a bulb, an air conditioner is a high-load appliance that runs for hours at a stretch. The wire supplying it must be sized correctly — not just for the AC to work, but for it to work safely, year after year.",
      },
      {
        type: "h2",
        text: "Why AC Wiring is Different from Regular House Wiring",
      },
      {
        type: "p",
        text: "A standard 5-amp power socket is designed for loads up to around 1,000W. Your 1.5-ton AC draws 1,200–1,500W continuously — often for 8–12 hours a day in Indian summers. That sustained high current generates heat in the wire. If the wire is undersized, that heat builds up inside the wall, slowly degrading the insulation, until a fault occurs.",
      },
      {
        type: "p",
        text: "AC circuits also experience inrush current on startup — a brief spike 3–5× the running current — which adds additional stress on the wiring. This is why a dedicated circuit with correctly sized wire is always recommended for air conditioners.",
      },
      {
        type: "h2",
        text: "What Wire Size Do You Need for Your AC?",
      },
      {
        type: "p",
        text: "Wire sizing for AC is based on the running current draw. Here is the standard recommendation for Indian residential split ACs:",
      },
      {
        type: "table",
        headers: ["AC Capacity", "Approx. Running Current", "Recommended Wire Size"],
        rows: [
          ["1 Ton (3.5 kW)", "5 – 7A", "2.5 sq.mm copper"],
          ["1.5 Ton (5.3 kW)", "7 – 9A", "2.5 sq.mm copper"],
          ["2 Ton (7 kW)", "9 – 13A", "4 sq.mm copper"],
          ["Cassette / Commercial", "13 – 18A", "4 – 6 sq.mm copper"],
        ],
      },
      {
        type: "callout",
        variant: "warning",
        text: "⚠️ Most Dangerous Mistake: Many electricians reuse existing 1.5 sq.mm wiring (installed for lighting circuits) for new AC points. A 1.5 sq.mm wire is rated for approximately 10A — dangerously close to the AC's running current and well below the startup spike. This is a leading cause of burnt sockets and wall fires in Indian homes.",
      },
      {
        type: "h3",
        text: "Why 2.5 sq.mm is the Standard for Most Home ACs",
      },
      {
        type: "p",
        text: "For 1 ton and 1.5 ton ACs — which cover the vast majority of Indian homes — a 2.5 sq.mm copper wire is the right choice. It is rated to carry 16–18A continuously, providing a comfortable safety margin above the AC's 7–9A running current. It handles startup inrush without strain and remains cool inside the wall even during extended operation.",
      },
      {
        type: "h2",
        text: "Always Use Copper — Not Aluminium",
      },
      {
        type: "p",
        text: "For any fixed wiring in a building — and especially for high-load circuits like AC — copper is the correct conductor material. Here's why:",
      },
      {
        type: "ul",
        items: [
          "Copper has lower resistance than aluminium, so it carries the same current with less heat generation",
          "Copper connections don't loosen over time the way aluminium connections do due to thermal expansion",
          "IS 694 (the Indian Standard for building wires) specifies copper conductors for house wiring",
          "Aluminium wiring requires larger cross-sections to carry the same current, making it impractical for domestic use",
        ],
      },
      {
        type: "h2",
        text: "FR or FRLS Wire for AC Circuits?",
      },
      {
        type: "p",
        text: "Both FR (Flame Retardant) and FRLS (Flame Retardant Low Smoke) grades are suitable for AC wiring. The choice depends on the building type:",
      },
      {
        type: "ul",
        items: [
          "Individual homes and villas: FR grade IS 694 wire is acceptable",
          "Apartments and multi-storey buildings: FRLS grade is strongly recommended — if there's a short circuit, FRLS insulation resists fire and produces significantly less toxic smoke, giving residents more time to evacuate",
          "Commercial spaces, offices, hospitals: FRLS or ZHFR (Zero Halogen Flame Retardant) is the specification",
        ],
      },
      {
        type: "h2",
        text: "The ISI Mark — Non-Negotiable for AC Wiring",
      },
      {
        type: "p",
        text: "Always insist on ISI-marked wire for AC circuits. The ISI mark (Bureau of Indian Standards certification) means the wire has been independently tested for conductor cross-section accuracy, insulation thickness, and flame performance. Uncertified 'local' wire often uses undersized conductors — saving copper at the cost of your safety.",
      },
      {
        type: "p",
        text: "Under IS 694, the actual conductor cross-section must be within a narrow tolerance of the stated size. A '2.5 sq.mm' local wire might actually contain 2.1–2.2 sq.mm of copper — reducing its current capacity and increasing heat generation without the buyer knowing.",
      },
      {
        type: "h2",
        text: "Angel Cables for AC Wiring — Direct from Manufacturer in Delhi",
      },
      {
        type: "p",
        text: "Angel Cables manufactures ISI-certified FR and FRLS copper house wires at our factory in Lawrence Road Industrial Area, Delhi. Our range covers 1.0 sq.mm to 6.0 sq.mm, including the 2.5 sq.mm and 4 sq.mm sizes most used for AC circuits.",
      },
      {
        type: "p",
        text: "Because we manufacture in Delhi and sell direct from factory — with no distributor or retailer markup — our pricing is consistently 15–20% below the major branded alternatives. Electricians, contractors and project managers across Delhi NCR, Haryana, UP and Rajasthan source their AC wiring from us for exactly this reason: full ISI quality at factory-direct pricing.",
      },
      {
        type: "ul",
        items: [
          "ISI-marked (IS 694) — meets all Indian safety standards",
          "100% copper conductor — no compromise on conductivity or current capacity",
          "FR and FRLS grades available — choose what your building type requires",
          "Available in 90m, 180m drums and custom bulk lengths",
          "Same-day dispatch for orders within Delhi",
          "GST invoices with HSN codes provided for all orders",
        ],
      },
      {
        type: "cta",
        heading: "Sourcing AC Wire for a Project?",
        text: "Get factory-direct pricing on ISI-certified 2.5 sq.mm and 4 sq.mm FRLS wire. We supply contractors, builders and dealers across North India.",
        buttonText: "Get a Bulk Quote",
        buttonLink: "/contact",
      },
      {
        type: "h2",
        text: "Checklist: Getting AC Wiring Right",
      },
      {
        type: "ol",
        items: [
          "Use a dedicated circuit for each AC — don't share with other loads",
          "Size the wire correctly: 2.5 sq.mm for 1–1.5 ton, 4 sq.mm for 2 ton",
          "Use copper conductor wire only",
          "Insist on ISI-marked (IS 694) wire",
          "Use FRLS grade in apartments and multi-storey buildings",
          "Install an appropriately rated MCB on the AC circuit (16A for 1.5 ton, 20A for 2 ton)",
          "Have all wiring done by a licensed electrician",
        ],
      },
      {
        type: "p",
        text: "Getting the wiring right the first time costs almost nothing extra. Getting it wrong can cost far more — in repairs, in damage, and in risk to your family.",
      },
    ],
  },
  {
    id: 2,
    slug: "fr-vs-frls-house-wire-india-guide",
    title: "FR vs FRLS Wire: Which House Wire Should You Choose in India?",
    excerpt:
      "FR and FRLS wires look identical on a drum but perform very differently when it matters most. Here's the complete breakdown — what the difference actually is, which buildings need which grade, and what to check before you buy.",
    category: "Wiring Guides",
    date: "May 10, 2025",
    readTime: "8 min read",
    image: null,
    content: [
      {
        type: "p",
        text: "Walk into any electrical wholesale market in Delhi or Mumbai and you'll find FR and FRLS wire stacked side by side, often from the same manufacturer. They look identical. The drums are the same colour. The labelling is similar. But the difference between them is the difference between a small electrical fire that goes out on its own — and one that spreads through a building.",
      },
      {
        type: "h2",
        text: "What is FR Wire?",
      },
      {
        type: "p",
        text: "FR stands for Flame Retardant. FR-grade PVC insulation is formulated to resist igniting when exposed to a flame source. When the flame is removed, the wire stops burning — it doesn't continue to propagate fire along its length. This is the baseline requirement under IS 694, the Indian Standard for PVC insulated cables for working voltages up to and including 1100V.",
      },
      {
        type: "p",
        text: "FR wire is tested under the IEC 60332-1 standard — a single-cable vertical flame test. A 600mm wire sample is exposed to a defined flame for 60 seconds. After the flame is removed, the charred portion must not exceed a certain length, confirming the wire won't keep burning.",
      },
      {
        type: "h2",
        text: "What is FRLS Wire?",
      },
      {
        type: "p",
        text: "FRLS stands for Flame Retardant Low Smoke. In addition to the flame retardancy of FR wire, FRLS insulation produces significantly less smoke when it burns. The compound used is designed to limit the density of smoke emissions — measured as a percentage of light obscuration in a closed chamber test.",
      },
      {
        type: "p",
        text: "Why does this matter? Because in the majority of fire fatalities, it is smoke inhalation — not direct contact with flames — that kills. Dense, toxic smoke from burning PVC insulation can incapacitate a person within minutes. FRLS wire significantly reduces this hazard, giving building occupants more time to detect the fire and evacuate.",
      },
      {
        type: "callout",
        variant: "info",
        text: "📋 Standard Compliance: FRLS wire meets the additional requirements of IS 694 for low smoke emission — typically smoke density (Ds) below 60% and acid gas emission below 20% by weight. Look for these specifications on the drum label when purchasing.",
      },
      {
        type: "h2",
        text: "FR vs FRLS — Side by Side",
      },
      {
        type: "table",
        headers: ["Property", "FR Wire", "FRLS Wire"],
        rows: [
          ["Flame retardant", "✓ Yes", "✓ Yes"],
          ["Low smoke emission", "✗ No", "✓ Yes"],
          ["Low acid gas emission", "✗ No", "✓ Yes (most grades)"],
          ["BIS / IS 694 compliant", "✓ Yes", "✓ Yes (higher spec)"],
          ["Single cable flame test", "✓ Passes", "✓ Passes"],
          ["Suitable for individual homes", "✓ Yes", "✓ Yes"],
          ["Suitable for apartments / high-rises", "⚠ Acceptable", "✓ Recommended"],
          ["Suitable for hospitals / schools", "✗ Not recommended", "✓ Required"],
          ["Typical price premium over FR", "Base", "~8–12% higher"],
        ],
      },
      {
        type: "h2",
        text: "Which Buildings Need Which Grade?",
      },
      {
        type: "h3",
        text: "Use FR Wire for:",
      },
      {
        type: "ul",
        items: [
          "Independent houses, villas, bungalows",
          "Small shops and single-floor offices",
          "Agricultural and farm buildings",
          "Temporary site installations",
          "Any single-occupancy building where occupants can self-evacuate quickly",
        ],
      },
      {
        type: "h3",
        text: "Use FRLS Wire for:",
      },
      {
        type: "ul",
        items: [
          "Apartment buildings and housing societies — especially above 3 floors",
          "Commercial complexes, malls and office buildings",
          "Hotels, guest houses and serviced apartments",
          "Hospitals, clinics, nursing homes",
          "Schools, colleges and educational institutions",
          "Any building where many people may be present simultaneously",
          "Government and CPWD projects (often mandatory under specifications)",
        ],
      },
      {
        type: "h2",
        text: "Wire Sizes for Home and Apartment Wiring",
      },
      {
        type: "p",
        text: "Choosing the right grade is only half the decision — correct sizing is equally important. Here is a standard reference for residential wiring under IS 694:",
      },
      {
        type: "table",
        headers: ["Circuit", "Typical Load", "Wire Size"],
        rows: [
          ["Lighting points", "Up to 800W", "1.5 sq.mm"],
          ["Fan points", "Up to 150W", "1.5 sq.mm"],
          ["5A power sockets", "Up to 1,000W", "2.5 sq.mm"],
          ["Air conditioner (1–1.5 ton)", "1,200–1,500W", "2.5 sq.mm"],
          ["Air conditioner (2 ton)", "1,800–2,200W", "4 sq.mm"],
          ["Geyser / water heater", "2,000W", "4 sq.mm"],
          ["Main sub-circuit / DB incoming", "Full floor load", "10–16 sq.mm"],
        ],
      },
      {
        type: "h2",
        text: "Why ISI Certification is Non-Negotiable",
      },
      {
        type: "p",
        text: "The ISI mark is not a formality — it is the only verifiable guarantee that the wire in your walls meets the specified conductor cross-section, insulation thickness, and flame performance. Without ISI certification, there is no independent verification of any of these properties.",
      },
      {
        type: "p",
        text: "Non-certified 'local' wire commonly cuts costs by:",
      },
      {
        type: "ul",
        items: [
          "Using underweight conductors (a '2.5 sq.mm' wire may contain only 2.1 sq.mm of copper)",
          "Thinning the insulation below the minimum specified in IS 694",
          "Using cheap PVC compound with poor flame retardancy that burns freely despite the 'FR' label",
          "Claiming FRLS rating without any actual low-smoke compound or testing",
        ],
      },
      {
        type: "p",
        text: "These shortcuts are invisible at the time of purchase — and only reveal themselves in the worst possible circumstances.",
      },
      {
        type: "h2",
        text: "Angel Cables House Wire — ISI Certified, Factory Direct from Delhi",
      },
      {
        type: "p",
        text: "Angel Cables manufactures IS 694-certified FR and FRLS copper house wires at our factory in the Lawrence Road Industrial Area, Delhi — one of Delhi's established manufacturing hubs for electrical products. Our wire range covers 1.0 sq.mm to 6.0 sq.mm in both FR and FRLS grades.",
      },
      {
        type: "p",
        text: "Every drum leaves our facility with a verifiable ISI mark. Our conductors are tested for cross-sectional area and resistance. Our insulation compounds are sourced from certified compounders and meet the IS 694 flame and smoke specifications.",
      },
      {
        type: "p",
        text: "And because we sell direct from factory — no wholesale distributor, no retail chain — our pricing is 15–20% below the major branded alternatives for the same ISI-certified product. Contractors and builders sourcing for 50-unit apartment complexes or 500-villa township projects consistently find that Angel Cables gives them full ISI quality at a price that improves their project margins.",
      },
      {
        type: "ul",
        items: [
          "FR and FRLS grades in all standard sizes (1.0 to 6.0 sq.mm)",
          "ISI marked — IS 694 certified",
          "100% electrolytic copper conductor",
          "Available in standard 90m and 180m drums, and bulk coil lengths",
          "GST invoices with HSN codes for all B2B orders",
          "Pan-India supply — based in Delhi with logistics to Haryana, UP, Rajasthan, Punjab and beyond",
        ],
      },
      {
        type: "cta",
        heading: "Building or Wiring a Project in North India?",
        text: "Get factory-direct pricing on ISI-certified FR and FRLS house wire. We supply contractors, developers and dealers at bulk rates.",
        buttonText: "Request a Quote",
        buttonLink: "/contact",
      },
      {
        type: "h2",
        text: "Quick Checklist Before You Buy House Wire",
      },
      {
        type: "ol",
        items: [
          "Check the drum for the ISI mark (IS 694) — don't accept wire without it",
          "Confirm the grade: FR for individual homes, FRLS for apartments and commercial",
          "Verify the conductor material is copper (not aluminium or CCA — copper-clad aluminium)",
          "Check the conductor cross-section matches the label — ask for a test certificate if buying in large quantity",
          "Buy from a manufacturer or authorised dealer — not from unknown local sources",
          "Keep the drum label or invoice as a record for insurance and warranty purposes",
        ],
      },
      {
        type: "p",
        text: "The wiring in a building is invisible once the walls are plastered — but it is working every single day. Getting it right at the time of construction is the only opportunity you have.",
      },
    ],
  },
  {
    id: 3,
    slug: "armoured-cable-guide-india",
    title: "What is Armoured Cable? When to Use It and How to Choose the Right Type",
    excerpt:
      "Armoured cable looks intimidating but the decision of when to use it — and which type — is straightforward once you understand the construction. Here's the complete guide for contractors, builders and project managers in India.",
    category: "Product Guides",
    date: "May 8, 2025",
    readTime: "8 min read",
    image: null,
    content: [
      {
        type: "p",
        text: "Most wiring inside a building uses flexible, PVC-insulated copper wire — protected by the walls, conduits and trunking around it. But the moment a cable leaves that protected environment — going underground, running outdoors between buildings, or entering an industrial panel — the rules change. That is where armoured cable comes in.",
      },
      {
        type: "p",
        text: "Armoured cables are built with a layer of steel wire or steel tape wrapped around the insulated cores, providing mechanical protection against crushing, rodent damage, and accidental impact. They are one of the most important — and most misspecified — cable types in the Indian market.",
      },
      {
        type: "h2",
        text: "How Armoured Cable is Constructed",
      },
      {
        type: "p",
        text: "A typical armoured cable has the following layers from the inside out:",
      },
      {
        type: "ol",
        items: [
          "Conductor — stranded copper (or aluminium for large sizes) carrying the current",
          "Core insulation — PVC or XLPE insulation around each conductor",
          "Filler — to make the cross-section circular and hold the cores in position",
          "Inner sheath — a PVC jacket binding the cores together before armouring",
          "Armour layer — steel wire armour (SWA) or steel tape armour (STA)",
          "Outer sheath — a final PVC jacket over the armour for UV and weather protection",
        ],
      },
      {
        type: "p",
        text: "The armour layer is the defining feature. It is what allows the cable to be buried directly in soil, run in open air, or installed in environments where a standard flexible cable would be damaged within weeks.",
      },
      {
        type: "h2",
        text: "SWA vs STA — What is the Difference?",
      },
      {
        type: "table",
        headers: ["Property", "Steel Wire Armour (SWA)", "Steel Tape Armour (STA)"],
        rows: [
          ["Construction", "Round steel wires wound helically", "Flat steel tape wound helically"],
          ["Mechanical strength", "Higher — handles crushing and impact", "Moderate — handles light mechanical stress"],
          ["Flexibility", "More flexible", "Less flexible"],
          ["Cable shape", "Round (all sizes)", "Round (small) or flat (multicore)"],
          ["Common use", "Underground, outdoor, industrial", "Control cables, lighter installations"],
          ["Indian standard", "IS 1554 / IS 7098", "IS 1554 / IS 7098"],
        ],
      },
      {
        type: "p",
        text: "For the vast majority of applications in India — underground power supply between buildings, site cabling, agricultural pump connections, industrial feeder cables — SWA is the correct choice. STA is more common in smaller control cables and signalling applications.",
      },
      {
        type: "h2",
        text: "Core Configurations: Which One Do You Need?",
      },
      {
        type: "p",
        text: "Armoured cables come in 2-core, 3-core, 3.5-core and 4-core configurations. Choosing the right one depends on the supply system:",
      },
      {
        type: "table",
        headers: ["Configuration", "Use Case"],
        rows: [
          ["2-core", "Single-phase supply (line + neutral), pumps, lighting"],
          ["3-core", "Three-phase supply without neutral (motors, industrial equipment)"],
          ["3.5-core", "Three-phase + reduced neutral (standard for LT distribution in India)"],
          ["4-core", "Three-phase + full neutral (where neutral carries significant load)"],
        ],
      },
      {
        type: "callout",
        variant: "info",
        text: "📋 For most LT (low tension) power distribution in India — apartment block incoming supply, substation to DB connections, DG set output cables — 3.5-core SWA armoured cable is the standard specification. The 3 full-size conductors carry the three phases, and the half-size conductor serves as the neutral.",
      },
      {
        type: "h2",
        text: "When Should You Use Armoured Cable?",
      },
      {
        type: "h3",
        text: "Underground Installations",
      },
      {
        type: "p",
        text: "This is the primary use case. When a cable needs to run underground — between a distribution board and a pump, from the main feeder to a building, or across an agricultural plot — armoured cable is the correct specification. The steel armour protects against soil pressure, digging tools, and burrowing rodents.",
      },
      {
        type: "p",
        text: "IS 1554 specifies that cables for direct burial should have armour and an outer PVC sheath. The minimum burial depth in India is generally 750mm under footpaths and 1000mm under roads and areas subject to vehicle loading.",
      },
      {
        type: "h3",
        text: "Outdoor Above-Ground Runs",
      },
      {
        type: "p",
        text: "Cable running between buildings, along exterior walls, on cable trays exposed to weather, or across open yards should be armoured. Unarmoured cable in outdoor locations is vulnerable to physical damage and UV degradation over time.",
      },
      {
        type: "h3",
        text: "Industrial and Factory Environments",
      },
      {
        type: "p",
        text: "Inside factories, power plants, and industrial facilities — particularly anywhere there is heavy equipment, forklift traffic, or chemical exposure — armoured cable on cable trays is the standard. The armour absorbs impact that would destroy standard flexible wiring.",
      },
      {
        type: "h3",
        text: "Agricultural Pump Connections",
      },
      {
        type: "p",
        text: "Connecting submersible or surface pumps across fields — where the cable lies on or under the ground — is a very common use of 2-core and 3-core armoured cable across rural India. The cable takes the physical punishment of farming operations that would quickly destroy unarmoured alternatives.",
      },
      {
        type: "h2",
        text: "Reading Armoured Cable Specifications",
      },
      {
        type: "p",
        text: "When ordering armoured cable, you will typically specify it as: [Cores] × [Cross-section] sq.mm [Armour type] [Standard]. For example:",
      },
      {
        type: "ul",
        items: [
          "3.5C × 16 sq.mm SWA XLPE — 3.5-core, 16 sq.mm conductors, steel wire armour, XLPE insulation",
          "4C × 10 sq.mm SWA PVC — 4-core, 10 sq.mm, steel wire armour, PVC insulation",
          "2C × 4 sq.mm SWA PVC — 2-core, 4 sq.mm, for single-phase pump connections",
        ],
      },
      {
        type: "p",
        text: "XLPE (cross-linked polyethylene) insulation handles higher temperatures than PVC and is preferred for larger cables and installations where the cable may run warm under load. For most standard applications up to 95 sq.mm, both PVC and XLPE are suitable — XLPE is specified for higher performance requirements.",
      },
      {
        type: "h2",
        text: "Common Sizing Guide for Armoured Cable",
      },
      {
        type: "table",
        headers: ["Application", "Typical Cable Size"],
        rows: [
          ["Single-phase pump (1–2 HP)", "2C × 2.5 sq.mm SWA"],
          ["Single-phase pump (3–5 HP)", "2C × 4–6 sq.mm SWA"],
          ["3-phase motor (5–10 HP)", "3C × 4 sq.mm SWA"],
          ["3-phase motor (15–25 HP)", "3C × 10–16 sq.mm SWA"],
          ["Apartment incoming supply (100A)", "3.5C × 35 sq.mm SWA"],
          ["Apartment incoming supply (200A)", "3.5C × 70 sq.mm SWA"],
          ["DG set output (63 KVA)", "3.5C × 25 sq.mm SWA"],
        ],
      },
      {
        type: "callout",
        variant: "warning",
        text: "⚠️ Always size armoured cable with a derating factor for underground installation — buried cable dissipates heat less efficiently than cable in air. As a rule, go one size up from the calculation for runs longer than 30m or in hot climates. Consult IS 1554 or a licensed electrical engineer for critical installations.",
      },
      {
        type: "h2",
        text: "Angel Cables Armoured Cable — Direct from Manufacturer in Delhi",
      },
      {
        type: "p",
        text: "Angel Cables manufactures SWA armoured cables at our Delhi factory, supplying contractors, builders, industrial buyers and agricultural dealers across North India. Our armoured cable range covers 2-core through 4-core configurations in conductor sizes from 1.5 sq.mm to 95 sq.mm, in both PVC and XLPE insulation.",
      },
      {
        type: "p",
        text: "Buying armoured cable direct from a Delhi manufacturer rather than through wholesale distribution typically saves 12–20% on project costs — significant on large cable quantities. We supply on reels with documentation including test certificates, ISI/BIS compliance, and GST invoices with HSN codes.",
      },
      {
        type: "ul",
        items: [
          "SWA armoured cable — 2C, 3C, 3.5C and 4C configurations",
          "Conductor sizes: 1.5 sq.mm to 95 sq.mm",
          "PVC and XLPE insulation options",
          "IS 1554 Part 1 (PVC) and IS 7098 (XLPE) compliant",
          "Copper conductor standard; aluminium available for large sizes",
          "Factory-direct pricing for contractors and bulk buyers",
          "Pan-India supply — based in Lawrence Road Industrial Area, Delhi",
        ],
      },
      {
        type: "cta",
        heading: "Need Armoured Cable for a Project?",
        text: "Get factory-direct pricing on SWA armoured cable. We supply contractors, electrical consultants and industrial buyers across North India.",
        buttonText: "Get a Bulk Quote",
        buttonLink: "/contact",
      },
    ],
  },
  {
    id: 4,
    slug: "submersible-cable-borewell-india",
    title: "How to Choose the Right Submersible Cable for Borewell Pumps in India",
    excerpt:
      "The cable connecting your submersible pump to the power supply is permanently submerged in water. Choosing the wrong one doesn't just cause breakdowns — it creates serious electrical hazards. Here's what you need to know.",
    category: "Product Guides",
    date: "May 6, 2025",
    readTime: "7 min read",
    image: null,
    content: [
      {
        type: "p",
        text: "India has over 30 million borewell pumps — in homes, farms, hotels, hospitals and industrial facilities. The submersible cable is one of the most stressed components in the entire installation: permanently submerged, exposed to ground minerals, pulled tight under its own weight, and carrying continuous electrical load.",
      },
      {
        type: "p",
        text: "Yet it is also one of the most under-specified. Many installations use whatever cable is cheapest or most available — ordinary PVC-insulated wire that was never designed for submersible use. The result is insulation breakdown, ground faults, tripped MCBs, damaged pumps, and in some cases electrocution from stray current in the water.",
      },
      {
        type: "h2",
        text: "What Makes a Cable 'Submersible'?",
      },
      {
        type: "p",
        text: "A genuine submersible cable is specifically engineered for permanent water immersion. The key differences from ordinary house wire are:",
      },
      {
        type: "ul",
        items: [
          "Water-resistant insulation compound — standard PVC absorbs moisture over time and loses insulation resistance. Submersible-grade PVC or modified polyethylene compounds resist water ingress even after years of submersion.",
          "Flat profile construction — most submersible cables are flat (2-core or 3-core side by side) rather than round. This allows them to hang straight in a narrow borewell casing without twisting under the pump's weight.",
          "Higher insulation thickness — to withstand the hydrostatic pressure at depth and the abrasion of the borewell casing wall.",
          "Tightly bonded cores — the cores are bonded together so the cable remains flat and doesn't separate when under tension.",
        ],
      },
      {
        type: "h2",
        text: "Single Phase vs Three Phase Submersible Cable",
      },
      {
        type: "p",
        text: "The first decision is matching the cable to the pump's electrical supply:",
      },
      {
        type: "table",
        headers: ["Supply Type", "Cable Configuration", "Typical Pump Size"],
        rows: [
          ["Single phase (230V)", "2-core flat submersible cable", "0.5 HP to 2 HP"],
          ["Three phase (415V)", "3-core flat submersible cable", "2 HP and above"],
          ["Single phase with earth", "3-core (L+N+E) submersible", "Higher safety installations"],
        ],
      },
      {
        type: "p",
        text: "For most domestic borewells in India — supplying a home or a small farm — a single-phase 2-core submersible cable is the standard specification. For agricultural pumps above 2 HP and for industrial or commercial borewells, 3-core three-phase submersible cable is used.",
      },
      {
        type: "h2",
        text: "What Wire Size Do You Need?",
      },
      {
        type: "p",
        text: "Sizing the cable correctly is critical. An undersized cable will run hot, cause voltage drop at the pump motor (leading to low torque and overheating), and fail prematurely. The depth of the borewell matters — a longer cable has higher resistance and requires a larger conductor to maintain adequate voltage at the pump.",
      },
      {
        type: "table",
        headers: ["Pump Rating", "Borewell Depth", "Recommended Cable Size"],
        rows: [
          ["0.5 HP single phase", "Up to 100 ft", "2C × 1.0 sq.mm"],
          ["1 HP single phase", "Up to 150 ft", "2C × 1.5 sq.mm"],
          ["1.5 HP single phase", "Up to 200 ft", "2C × 2.5 sq.mm"],
          ["2 HP single phase", "Up to 200 ft", "2C × 2.5 sq.mm"],
          ["3 HP three phase", "Up to 300 ft", "3C × 2.5 sq.mm"],
          ["5 HP three phase", "Up to 300 ft", "3C × 4 sq.mm"],
          ["7.5 HP three phase", "Up to 400 ft", "3C × 6 sq.mm"],
          ["10 HP three phase", "Up to 400 ft", "3C × 10 sq.mm"],
        ],
      },
      {
        type: "callout",
        variant: "warning",
        text: "⚠️ For deeper borewells (300ft+), always go one conductor size up from the table above. The additional resistance of the longer cable causes voltage drop that starves the pump motor of voltage — causing it to draw more current, overheat, and fail. The cost of a larger cable is a fraction of replacing a burnt pump motor.",
      },
      {
        type: "h2",
        text: "Key Things to Check When Buying Submersible Cable",
      },
      {
        type: "h3",
        text: "1. Genuine Submersible Grade — Not Ordinary PVC Wire",
      },
      {
        type: "p",
        text: "This is the most important check. A common substitution in the market is ordinary 2-core PVC wire sold as 'submersible cable'. Standard PVC insulation is not designed for permanent water immersion — it will absorb moisture, the insulation resistance will drop, and ground faults will develop within 1–3 years.",
      },
      {
        type: "p",
        text: "Genuine submersible cable will be flat, have bonded cores, and be clearly labelled as 'submersible pump cable' or marked to IS 694 submersible specification. The outer surface should feel slightly different — denser and with a more uniform finish than standard house wire.",
      },
      {
        type: "h3",
        text: "2. ISI Mark",
      },
      {
        type: "p",
        text: "Insist on ISI-marked submersible cable. The BIS ISI mark confirms the cable has been tested for water resistance, insulation thickness, and conductor cross-section under the relevant Indian standard. Non-certified submersible cable has no verified performance guarantee.",
      },
      {
        type: "h3",
        text: "3. Pure Copper Conductor",
      },
      {
        type: "p",
        text: "Always specify copper conductor for submersible cables. Aluminium has higher resistance (meaning more voltage drop over the length of the cable), is less durable in wet environments, and connections to pump terminals loosen more easily with aluminium. Some low-quality cable uses CCA (copper-clad aluminium) — a cost-cutting measure that gives poor performance at depth.",
      },
      {
        type: "h3",
        text: "4. Correct Cable Length",
      },
      {
        type: "p",
        text: "Measure the borewell depth and add at least 5–6 metres for the surface run to the starter panel. Use a single, unjointed run from pump to panel wherever possible — joints inside a borewell are a major failure point. Angel Cables supplies submersible cable in custom lengths to avoid the need for mid-run joints.",
      },
      {
        type: "h2",
        text: "Installation: A Few Critical Points",
      },
      {
        type: "ul",
        items: [
          "Never coil excess cable inside the borewell — it creates resistance hotspots and may jam the pump",
          "Secure the cable to the delivery pipe with cable ties every 1.5–2 metres so it doesn't hang free and stretch under its own weight",
          "The cable entry into the pump should be sealed with appropriate waterproof fittings",
          "All connections at the surface must be in a weatherproof enclosure — never leave terminals exposed to rain",
          "Install a proper motor protection relay or overload protector at the starter — this saves the pump if the cable develops a fault",
        ],
      },
      {
        type: "h2",
        text: "Angel Cables Submersible Cable — Manufactured in Delhi",
      },
      {
        type: "p",
        text: "Angel Cables manufactures ISI-certified submersible pump cables at our Delhi facility, supplying pump dealers, agricultural equipment suppliers, and contractors across North India. Our submersible cable range covers 1.0 sq.mm to 10 sq.mm in 2-core and 3-core flat configurations, in both single-phase and three-phase specifications.",
      },
      {
        type: "p",
        text: "Our submersible cables use water-resistant PVC insulation compounded specifically for permanent immersion, bonded flat-core construction for correct installation in narrow borewell casings, and 100% electrolytic copper conductors. Each drum is ISI-marked and supplied with GST documentation.",
      },
      {
        type: "p",
        text: "Being a manufacturer — not a trader — means we can supply exact custom lengths with no wastage, at pricing that is consistently better than retail alternatives. For pump dealers and agricultural suppliers buying in volume, we offer dealer pricing with priority supply.",
      },
      {
        type: "cta",
        heading: "Supply Submersible Pumps or Borewell Equipment?",
        text: "Get factory-direct pricing on ISI-certified submersible cable. Custom lengths available. We supply pump dealers and contractors across North India.",
        buttonText: "Talk to Our Team",
        buttonLink: "/contact",
      },
    ],
  },
  {
    id: 5,
    slug: "copper-vs-aluminium-wire-india",
    title: "Copper Wire vs Aluminium Wire: Why Copper is Always the Right Choice for Indian Homes",
    excerpt:
      "Aluminium wire is cheaper per metre. That is where its advantages end. Here is the complete breakdown of why copper is the correct choice for all fixed wiring in Indian buildings — and why the small price difference is one of the best investments in a home.",
    category: "Wiring Guides",
    date: "May 4, 2025",
    readTime: "6 min read",
    image: null,
    content: [
      {
        type: "p",
        text: "The question comes up on every construction project where the budget is tight: 'Can we use aluminium wire instead of copper? It's so much cheaper.' It's a reasonable question — aluminium wire can cost 40–50% less per metre than equivalent copper. But the comparison is misleading, and on fixed building wiring, aluminium is the wrong choice. Here is why.",
      },
      {
        type: "h2",
        text: "The Basic Electrical Difference",
      },
      {
        type: "p",
        text: "Copper and aluminium differ fundamentally in their electrical properties. Copper has an electrical resistivity of approximately 1.72 × 10⁻⁸ Ω·m. Aluminium's resistivity is 2.82 × 10⁻⁸ Ω·m — about 64% higher. In practical terms, to carry the same current with the same voltage drop and heat generation, an aluminium conductor needs to be about 1.6× larger in cross-section than a copper conductor.",
      },
      {
        type: "table",
        headers: ["Property", "Copper", "Aluminium"],
        rows: [
          ["Electrical resistivity", "1.72 × 10⁻⁸ Ω·m", "2.82 × 10⁻⁸ Ω·m"],
          ["Relative conductivity", "100%", "61%"],
          ["Equivalent cross-section for same current", "1× (baseline)", "1.6× larger needed"],
          ["Weight for equivalent conductor", "Heavier", "~50% lighter"],
          ["Tensile strength", "Higher", "Lower (more brittle)"],
          ["Thermal expansion", "Lower", "~35% higher"],
          ["Cost per metre (approx.)", "Higher", "40–50% lower"],
        ],
      },
      {
        type: "h2",
        text: "The Problem with Aluminium in Buildings: Connections",
      },
      {
        type: "p",
        text: "The resistivity difference alone might suggest aluminium is viable if you simply use a larger cross-section. But the real problem with aluminium wiring in buildings is not the conductor itself — it is what happens at every connection point.",
      },
      {
        type: "h3",
        text: "Thermal Expansion and Loose Connections",
      },
      {
        type: "p",
        text: "When electrical current flows, the conductor heats up. When current stops (lights off, appliance unplugged), it cools down. Every heating and cooling cycle causes the conductor to expand and contract. Aluminium expands approximately 35% more per degree than copper for the same temperature change.",
      },
      {
        type: "p",
        text: "Over years of daily cycles, aluminium connections — at switch boxes, socket terminals, junction boxes, and distribution boards — gradually work loose. A loose connection creates a high-resistance point. High resistance means more heat. More heat means more expansion and further loosening. This is a progressive deterioration that is invisible inside the wall — until the connection fails, arcs, and starts a fire.",
      },
      {
        type: "h3",
        text: "Oxidation",
      },
      {
        type: "p",
        text: "Aluminium oxidises when exposed to air, forming aluminium oxide on the conductor surface. Unlike copper oxide (which is slightly conductive), aluminium oxide is an electrical insulator. A thin oxide layer forms on any exposed aluminium surface within hours. At connection terminals, this oxide layer increases contact resistance — exactly the problem described above.",
      },
      {
        type: "p",
        text: "Copper also oxidises, but copper oxide is far less resistive and has minimal impact on connection quality over time.",
      },
      {
        type: "callout",
        variant: "warning",
        text: "⚠️ In the 1960s and 70s, aluminium wiring was widely used in residential construction in the USA. By the 1980s, it became clear that aluminium-wired homes had significantly higher rates of electrical fires. The US Consumer Product Safety Commission documented that homes with aluminium wiring were 55 times more likely to have a fire hazard condition at connections than copper-wired homes.",
      },
      {
        type: "h2",
        text: "What Indian Standards Say",
      },
      {
        type: "p",
        text: "IS 694 — the Bureau of Indian Standards specification for PVC insulated cables for working voltages up to 1100V — specifies copper conductors for house wiring. The standard's scope and specifications are written around copper's properties.",
      },
      {
        type: "p",
        text: "IS 8130, which covers conductors for insulated electrical cables and flexible cords, defines copper as the primary material for conductors in fixed building wiring. CPWD (Central Public Works Department) specifications for government construction projects mandate copper conductors throughout.",
      },
      {
        type: "h2",
        text: "Where Aluminium is Actually Used — And Why",
      },
      {
        type: "p",
        text: "Aluminium is not useless in electrical systems — it is just wrong for building wiring. Here is where it is correctly specified:",
      },
      {
        type: "ul",
        items: [
          "Overhead transmission and distribution lines — aluminium's light weight is a genuine advantage for long spans between poles, where conductor weight drives tower design and cost",
          "Large underground feeder cables (above 95 sq.mm) — where the cost saving on very large conductors is significant and special aluminium-rated connectors are used throughout",
          "Transformer windings in some applications — where the design accounts for aluminium's properties specifically",
        ],
      },
      {
        type: "p",
        text: "None of these are situations you encounter in residential or commercial building wiring.",
      },
      {
        type: "h2",
        text: "The Real Cost Comparison",
      },
      {
        type: "p",
        text: "Let us be specific about the cost difference on a typical Indian home. A 1,000 sq.ft 2BHK apartment might use approximately 400–500 metres of house wire across all circuits. The cost difference between copper and aluminium for this quantity is roughly ₹3,000–5,000 — less than 1% of the construction cost of the flat.",
      },
      {
        type: "p",
        text: "Against this, consider the consequences of an aluminium wiring failure: rewiring a flat after walls are plastered costs ₹30,000–80,000 and involves breaking open walls throughout the home. Fire damage and insurance implications are far beyond that.",
      },
      {
        type: "p",
        text: "The copper 'premium' is not a premium at all relative to the cost of the building it protects.",
      },
      {
        type: "h2",
        text: "One More Thing: CCA Wire",
      },
      {
        type: "p",
        text: "There is a third option that has appeared in the Indian market — CCA (Copper-Clad Aluminium) wire. It has a thin outer layer of copper over an aluminium core, giving the appearance of copper wire at lower cost. CCA wire fails in the same ways as aluminium wire at connections, has significantly higher resistance than true copper, and — critically — can be difficult to identify visually once it is inside a wall.",
      },
      {
        type: "p",
        text: "ISI-certified wire uses pure copper conductors. If you are buying from an unknown source or an unusually cheap product, a simple test is to cut the conductor end and look at the cross-section under light — CCA wire shows a clear aluminium-coloured core with a thin copper ring around it.",
      },
      {
        type: "h2",
        text: "Angel Cables Copper Wire — 100% Electrolytic Copper, ISI Certified",
      },
      {
        type: "p",
        text: "Every Angel Cables product uses 100% electrolytic copper conductor — the highest-purity copper used in electrical applications. We do not use CCA, aluminium, or recycled conductor that may have inconsistent composition. Our conductors are tested for cross-sectional area and electrical resistance to confirm they meet IS 694 specifications.",
      },
      {
        type: "p",
        text: "Our copper house wire range (FR and FRLS, 1.0 to 6.0 sq.mm) and our bare copper wire products are manufactured at our Delhi factory and supplied direct to contractors, builders and dealers across North India — at pricing that is factory-direct, with no retail markup.",
      },
      {
        type: "cta",
        heading: "Want to Confirm You're Getting Real Copper Wire?",
        text: "Call or message us — we supply ISI-certified, 100% electrolytic copper wire direct from our Delhi factory. Ask for a test certificate with your order.",
        buttonText: "Contact Us",
        buttonLink: "/contact",
      },
    ],
  },
  {
    id: 6,
    slug: "common-house-wiring-mistakes-india",
    title: "8 Common House Wiring Mistakes in India — And How to Avoid Every One",
    excerpt:
      "Most electrical problems in Indian homes — burnt sockets, tripping MCBs, shocks from appliances — trace back to a handful of wiring mistakes made during construction. Here they are, and here is how to make sure they don't happen in your project.",
    category: "Safety Guides",
    date: "May 2, 2025",
    readTime: "9 min read",
    image: null,
    content: [
      {
        type: "p",
        text: "Once the walls are plastered and painted, the wiring inside is invisible and inaccessible. Whatever decisions were made during construction — good or bad — are locked in for the life of the building. This makes the wiring phase one of the most consequential parts of any construction project, and also one where mistakes are routinely made in the interest of saving time or money.",
      },
      {
        type: "p",
        text: "Here are the eight most common wiring mistakes seen in Indian residential and commercial construction — what causes them, what they lead to, and how to prevent them.",
      },
      {
        type: "h2",
        text: "Mistake 1: Using the Same Wire Size for Every Circuit",
      },
      {
        type: "p",
        text: "The most widespread mistake. Many electricians and contractors default to 1.5 sq.mm wire throughout a building — for lighting, fans, sockets, ACs and geysers alike. 1.5 sq.mm wire is rated for approximately 10A. A 1.5-ton AC draws 7–9A running, with a startup spike well above that. A geyser draws 8–10A continuously. Using 1.5 sq.mm for these circuits creates wiring that runs near its rated limit for hours daily.",
      },
      {
        type: "p",
        text: "The result is insulation that ages prematurely, sockets that get warm or discoloured, and MCBs that trip without a clear reason. In the worst case, the wire overheats inside the wall.",
      },
      {
        type: "p",
        text: "The fix is straightforward — use the right wire size for each circuit:",
      },
      {
        type: "table",
        headers: ["Circuit", "Correct Wire Size"],
        rows: [
          ["Lighting and fans", "1.5 sq.mm"],
          ["Power sockets (5A)", "2.5 sq.mm"],
          ["Air conditioner (1–1.5 ton)", "2.5 sq.mm"],
          ["Air conditioner (2 ton)", "4 sq.mm"],
          ["Geyser / water heater", "4 sq.mm"],
          ["Oven / microwave", "4 sq.mm"],
          ["Main sub-circuit", "6–10 sq.mm"],
        ],
      },
      {
        type: "h2",
        text: "Mistake 2: Using Non-ISI Certified Wire to Save ₹2–3 Per Metre",
      },
      {
        type: "p",
        text: "The price difference between ISI-certified house wire and uncertified local wire is small — typically ₹2–5 per metre. On a 2BHK apartment with 400m of wire, that is a saving of ₹800–2,000 on a project costing ₹30–50 lakh. It is not a meaningful saving.",
      },
      {
        type: "p",
        text: "What the saving buys is wire with an unverified conductor cross-section (frequently undersized by 10–15%), insulation of unknown thickness and flame performance, and no accountability from the manufacturer. ISI certification exists precisely to prevent this class of problem.",
      },
      {
        type: "callout",
        variant: "warning",
        text: "⚠️ A simple check: weigh a 100m drum of wire. An ISI-compliant 2.5 sq.mm wire drum should weigh a specific amount based on copper density and conductor cross-section. Significantly lighter weight = less copper = not what the label says.",
      },
      {
        type: "h2",
        text: "Mistake 3: No Dedicated Circuit for High-Load Appliances",
      },
      {
        type: "p",
        text: "Running an AC, a geyser and a washing machine from the same circuit is a recipe for tripped MCBs and overloaded wiring. Every high-load appliance (AC, geyser, oven, EV charger) should have a dedicated circuit — its own wire from the distribution board to the dedicated socket or direct connection.",
      },
      {
        type: "p",
        text: "Shared circuits cause problems even when individual loads are within the wire's rating, because diversity — multiple loads switching on simultaneously — can momentarily exceed the MCB rating. A dedicated circuit eliminates this problem and also makes fault diagnosis vastly simpler.",
      },
      {
        type: "h2",
        text: "Mistake 4: Skipping Earthing or Using Inadequate Earthing",
      },
      {
        type: "p",
        text: "Earthing is the single most important safety feature in any electrical installation. A proper earth connection ensures that if a live wire touches a metal surface — an appliance body, a metal conduit, a water pipe — the fault current flows safely to earth and trips the MCB, rather than waiting for a person to complete the circuit.",
      },
      {
        type: "p",
        text: "Common earthing failures include: not running a separate earth wire to metal-bodied appliances, sharing an earth conductor across too many circuits, using undersized earth wire, and making earth connections at junction boxes rather than running dedicated earth conductors back to the main earth terminal.",
      },
      {
        type: "p",
        text: "Symptoms of inadequate earthing: mild electric shocks from metal taps, geysers or appliance bodies, RCDs tripping repeatedly without a clear reason, and appliances seeming to have a 'tingle' when touched.",
      },
      {
        type: "h2",
        text: "Mistake 5: Overloading Junction Boxes with Too Many Connections",
      },
      {
        type: "p",
        text: "Junction boxes inside walls accumulate over time as modifications are made — adding a socket here, extending a circuit there. Each connection is a potential failure point. Connections made poorly (conductors not fully inserted, insulation tape used instead of proper connectors) create resistance, and resistance creates heat.",
      },
      {
        type: "p",
        text: "Best practice: minimise the number of mid-run joints by running wire in continuous lengths from the DB to the point. Where joints are necessary, use proper screw-type or spring connectors in an accessible junction box — not buried behind plaster.",
      },
      {
        type: "h2",
        text: "Mistake 6: Using Aluminium Wire or CCA Wire",
      },
      {
        type: "p",
        text: "Aluminium wire is cheaper than copper wire and has been used in Indian construction to cut costs. The problem — covered in detail in our guide on copper vs aluminium wire — is that aluminium connections loosen over time due to the metal's higher thermal expansion, and aluminium oxide at terminals creates increasing contact resistance that eventually leads to connection failures and fires.",
      },
      {
        type: "p",
        text: "IS 694, the Indian standard for house wiring, specifies copper conductors. If you are unsure what wire is in your building — particularly in older properties — the conductor colour at a cut end tells you immediately: bright orange-red is copper; silver-grey is aluminium.",
      },
      {
        type: "h2",
        text: "Mistake 7: Incorrect MCB Sizing",
      },
      {
        type: "p",
        text: "The MCB (Miniature Circuit Breaker) is the wire's protection device. It should trip before the wire overheats. If the MCB is oversized relative to the wire, the wire will overheat before the MCB trips — defeating the entire purpose.",
      },
      {
        type: "p",
        text: "The MCB rating must be matched to the wire, not the load. The wire dictates the MCB rating:",
      },
      {
        type: "table",
        headers: ["Wire Size", "Maximum MCB Rating"],
        rows: [
          ["1.5 sq.mm", "10A MCB"],
          ["2.5 sq.mm", "16A MCB"],
          ["4 sq.mm", "20A MCB"],
          ["6 sq.mm", "32A MCB"],
          ["10 sq.mm", "40–50A MCB"],
        ],
      },
      {
        type: "p",
        text: "A very common mistake is installing a 16A MCB on 1.5 sq.mm wire to 'avoid nuisance tripping'. This is dangerous — the MCB will allow the wire to carry more than it should before tripping.",
      },
      {
        type: "h2",
        text: "Mistake 8: No Provision for Future Loads",
      },
      {
        type: "p",
        text: "Indian homes in 2025 have far more electrical load than homes built in 2005. Air conditioners, EV chargers, induction cooktops, dishwashers — these loads were not anticipated in older wiring designs. Today's new construction should anticipate the loads of 2035: provision for EV charging in parking, circuits for future split ACs in every bedroom, spare MCB slots in the distribution board.",
      },
      {
        type: "p",
        text: "Adding a circuit after construction means breaking open walls. The cost of a few extra metres of wire and an extra MCB slot during construction is negligible compared to the cost and disruption of retrofitting.",
      },
      {
        type: "h2",
        text: "Avoiding These Mistakes Starts with the Right Materials",
      },
      {
        type: "p",
        text: "Most of the mistakes above are compounded when the wire itself is substandard. An undersized conductor is more affected by overloading. Non-ISI insulation is less tolerant of heat. A poor connection on a lightweight wire is more prone to failure.",
      },
      {
        type: "p",
        text: "Angel Cables manufactures ISI-certified FR and FRLS copper house wire in all standard sizes — 1.0 sq.mm to 6.0 sq.mm — at our Delhi factory. Using the correct wire size for each circuit, from a manufacturer whose ISI certification is verifiable, eliminates the materials-side risk in all of the mistakes above.",
      },
      {
        type: "ul",
        items: [
          "ISI-marked (IS 694) — verified conductor cross-section and insulation",
          "100% electrolytic copper conductor — not CCA or aluminium",
          "FR and FRLS grades — fire-safe insulation compound",
          "All standard sizes in stock — 1.0 to 6.0 sq.mm",
          "Factory-direct pricing — consistent quality without the retail markup",
        ],
      },
      {
        type: "cta",
        heading: "Specifying Wire for a New Construction Project?",
        text: "Get the right wire sizes for every circuit, with ISI certification and factory-direct pricing. We supply builders, contractors and electricians across North India.",
        buttonText: "Get a Quote",
        buttonLink: "/contact",
      },
    ],
  },
];

export default blogs;
